/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useState } from "react";
import jsepEval from "@sensative/jsep-eval";

import { api, PrinterLabelType } from "../../logic/api";

import { SessionContext } from "../../utility/useSession";
import { retrieveValue } from "../../utility/storage";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { IBrowserPrintDevice } from "../thirdParty/zebra-browser-print";

const loadBrowserPrint = (): Promise<any> => {
    return new Promise((resolve, reject) => {
        if ((window as any).BrowserPrint) {
            resolve((window as any).BrowserPrint);
            return;
        }

        const script = document.createElement('script');
        script.src = '/zebra-browser-print.js';
        script.type = 'text/javascript';

        script.onload = () => {
            const BrowserPrint = (window as any).BrowserPrint;
            if (BrowserPrint) {
                resolve(BrowserPrint);
            } else {
                reject(new Error('BrowserPrint not found after script load'));
            }
        };

        document.head.appendChild(script);
    });
};

export interface IPrintJob {
    type: PrinterLabelType;
    arrayOfValues?: Record<string, unknown>[]; // Controls the number of labels to print.. e.g [{},{}] prints two labels.. params get merged into ZPL.
    printer?: string;
}

export interface ILabelPrinter {
    print: (job: IPrintJob, callBack?: () => void) => void;
    busy: boolean;
    err: Error | unknown | null;
}

interface ILabelPrinterHelpers {
    labelPrinter: ILabelPrinter;
    getZPLParameters: (zpl: string) => string[];
    conditionalZPLParameters: (zpl: string) => string;
    mergeZPLParameters: (zpl: string, values: Record<string, unknown>) => string;
}

export const useLabelPrinter = (): ILabelPrinterHelpers => {
    const session = useContext(SessionContext);
    const fetch = api.hooks.printerLabel.get();
    const [err, setErr] = useState<Error | unknown | null>(null);
    const [busy, setBusy] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (fetch.err) {
            setErr(fetch.err);
        }
    }, [fetch.err]);

    const labelPrinter: ILabelPrinter = {
        print: (job: IPrintJob, callBack?: () => void) => {
            setErr(null);
            setBusy(true);

            const defaultPrinter = retrieveValue("defaultLabelPrinterUID", "");

            if (job.printer == null || job.printer == "") {
                job.printer = defaultPrinter;
            }

            fetch.send(session.current.context, { type: job.type }, async (label) => {
                try {
                    if (label == null) {
                        throw Error(t("labels.labelNotFound", { type: PrinterLabelType[job.type] }))
                    }
                    if (label.zpl == null || label.zpl == "") {
                        throw Error(t("labels.labelNotConfig", { type: PrinterLabelType[job.type] }))
                    }
                    job.arrayOfValues = job.arrayOfValues || [];
                    if (job.arrayOfValues.length == 0) {
                        job.arrayOfValues.push({});
                    }
                    let zpl = "";
                    for (const values of job.arrayOfValues) {
                        zpl = `${zpl}${mergeZPLParameters(label.zpl, values || {})}\r\n`
                    }

                    await ZebraLabelPrinter.sendToPrinter(job.printer == null ? defaultPrinter : job.printer, zpl);
                } catch (ex) {
                    setErr(ex);
                    console.error(ex);
                }
                setBusy(false);

                if (callBack != null) {
                    callBack();
                }
            });
        },
        busy: busy == true,
        err: err
    }

    const getZPLParameters = (zpl: string) => {
        const parameters: string[] = [];
        const segments = (zpl || "").split("%");

        for (const s of segments) {
            if (s.startsWith("[")) {
                if (s.indexOf("]") != -1) {
                    const field = s.split("[")[1].split("]")[0];
                    if (field.length > 0) {
                        if (parameters.indexOf(field) == -1) {
                            parameters.push(field);
                        }
                    }
                }
            }
        }

        return parameters;
    }

    const conditionalZPLParameters = (zpl: string) => {
        zpl = zpl || "";

        let startTag = "#IF{";
        const endTag = "#END#";
        let startPos = 0;
        let endPos = 0;
        let condition = "";

        const parseExpression = (exp: string) => {
            try {
                const result = jsepEval.evaluate(exp.toLowerCase());
                return result == true || result == "true" || result == "1" || result == 1;
            } catch (e) {
                return "";
            }
        }

        while (true) {
            startPos = zpl.indexOf(startTag);
            if (startPos == -1) {
                break;
            }

            condition = zpl.substring(startPos);
            endPos = condition.indexOf(endTag);

            if (endPos == -1) {
                break;
            }

            endPos += endTag.length;
            condition = condition.substring(0, endPos);

            const conditionalValue = ((condition.split("{")[1] || "").split("}")[0] || "");
            const conditionalIf = `${startTag}${conditionalValue}}#`;
            const conditionalContent = condition.substring(conditionalIf.length - 1, condition.length - endTag.length);

            if (parseExpression(conditionalValue) == true) {
                zpl = zpl.replace(condition, conditionalContent);
            } else {
                zpl = zpl.replace(condition, "");
            }
        }

        startTag = "#IFNOT{";
        startPos = zpl.indexOf(startTag);
        endPos = 0;
        condition = "";

        while (true) {
            startPos = zpl.indexOf(startTag);

            if (startPos == -1) {
                break;
            }

            condition = zpl.substring(startPos);
            endPos = condition.indexOf(endTag);

            if (endPos == -1) {
                break;
            }

            endPos += endTag.length;
            condition = condition.substring(0, endPos);

            const conditionalValue = ((condition.split("{")[1] || "").split("}")[0] || "");
            const conditionalIf = `${startTag}${conditionalValue}}#`;
            const conditionalContent = condition.substring(conditionalIf.length - 1, condition.length - endTag.length);

            if (parseExpression(conditionalValue) == false) {
                zpl = zpl.replace(condition, conditionalContent);
            } else {
                zpl = zpl.replace(condition, "");
            }
        }

        return zpl;
    }

    const mergeZPLParameters = (zpl: string, values: Record<string, unknown>) => {
        zpl = zpl || "";

        const parameters = getZPLParameters(zpl);
        for (const param of parameters) {
            const field = `%[${param}]`;
            const val = values[param];

            // This is not feasible. There are many different params that end in `Date` which require
            // different formatting (e.g. with the time or without the time).
            // Additionally, this doesn't format values that need to only be a "time" (e.g. flightArrivalTime)
            // and leaves out dates that don't follow a specific key naming convention
            //
            // For any date/time formatting, it would be preferable to keep this in the relevant `labelTemplate` util
            // so that we can be explicit on what we expect these params to be formatted as and avoid unexpected behaviour.
            //
            // if(val!=null){
            //     if(param.toLowerCase().endsWith("date")){
            //         const date = new Date(val as string);
            //         val = toFormattedDateTimeString(date);
            //     }
            // }

            const value = (val == null ? "" : val as string).toString();
            while (zpl.indexOf(field) != -1 && field != value) {
                zpl = zpl.replace(field, value);
            }
        }

        return conditionalZPLParameters(zpl);
    }

    return {
        labelPrinter,
        getZPLParameters,
        conditionalZPLParameters,
        mergeZPLParameters,
    }
}

export class ZebraLabelPrinter {
    static async getPrinters() {
        return new Promise<IBrowserPrintDevice[]>(async (accept, reject) => {
            try {
                // Load BrowserPrint library
                const BrowserPrint = await loadBrowserPrint();

                BrowserPrint.getLocalDevices((result: any) => {
                    if (result == null || result.printer == null || result.printer.length == 0) {
                        reject(i18next.t("labels.noPrintersFound"));
                        return;
                    }
                    accept(result.printer || []);
                }, (errorMessage: string) => {
                    reject(errorMessage);
                });
            } catch (error) {
                console.error('Error loading BrowserPrint library:', error);
                reject(new Error('Failed to load BrowserPrint library'));
            }
        });
    }

    static async sendToPrinter(uid: string | null, zpl: string) {
        return new Promise<void>(async (accept, reject) => {
            console.debug(i18next.t("labels.sendingPrint"), zpl);

            ZebraLabelPrinter.getPrinters().then((printers) => {
                for (const device of printers) {
                    console.debug(i18next.t("labels.job"), uid);
                    if (device.uid == uid || uid == null || uid == "") {
                        device.send(zpl, () => {
                            accept();
                        }, (errorMessage) => {
                            console.error(i18next.t("labels.errorJob"));
                            reject(errorMessage);
                        });
                    }
                    return;
                }
                reject(i18next.t("noPrintersReturned"));
            }).catch((errorMessage) => {
                reject(errorMessage);
            });
        });
    }
}
